import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { Invitation, Organisation, Team, User as TPUser } from "../types";
import {
  cancelInvitation,
  deleteOrganisation,
  deleteTeam,
  getActivityLogs,
  getOrgInvitations,
  getTeams,
  getUsers,
  inviteUser,
  leaveOrganisation,
  removeMember,
  renameOrganisation,
  saveTeam,
  updateMemberRole,
} from "../services/storageService";
import { generateId } from "../services/utils";
import {
  ConfirmationModal,
  ErrorModal,
  InviteModal,
  RoleModal,
  SuccessModal,
} from "./Modals";
import {
  CheckCircle,
  History,
  LogOut,
  Mail,
  Plus,
  RotateCw,
  Search,
  Settings2,
  Shield,
  Trash2,
  Users,
  XCircle,
  Zap,
  Crown,
  ShieldAlert,
} from "./Icons";
import { Github } from "lucide-react";
import GithubInstallModal from "./GithubInstallModal";
import BillingTab from "./BillingTab";
import { UpgradePrompt } from "./UpgradePrompt";

interface TeamManagerProps {
  currentUser: TPUser;
  currentOrg: Organisation;
  onOpenCreateWorkspace: () => void;
}

const TeamManager: React.FC<TeamManagerProps> = ({
  currentUser,
  currentOrg,
  onOpenCreateWorkspace,
}) => {
  const { getToken } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [users, setUsers] = useState<TPUser[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityAction, setActivityAction] = useState("");
  const [activityOffset, setActivityOffset] = useState(0);
  const [hasMoreActivity, setHasMoreActivity] = useState(true);
  const [isActivityLoading, setIsActivityLoading] = useState(false);
  const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set());
  const activePromises = useRef<Record<string, Promise<any>>>({});

  const isMaintainer = currentOrg.role === "MAINTAINER";

  const isPersonal =
    currentOrg.type === "PERSONAL" ||
    (currentOrg.name === "My Workspace" && currentOrg.memberIds?.length === 1);
  const isOwner = currentOrg.ownerId === currentUser.id;

  const isShared = currentOrg.type === "SHARED";
  const isTrialExpired =
    currentOrg.subscriptionStatus === "TRIALING" &&
    (currentOrg.subscriptionCurrentPeriodEnd || 0) <= Date.now();
  const isActive =
    currentOrg.subscriptionStatus === "ACTIVE" ||
    (currentOrg.subscriptionStatus === "TRIALING" && !isTrialExpired);
  const isReadOnly = isShared && !isActive;

  const navigate = useNavigate();
  const { tabId } = useParams<{ tabId: string }>();

  // Valid tabs
  const validTabs = [
    "members",
    "teams",
    "integrations",
    "settings",
    "activity",
    "billing",
  ];

  // Determine active tab from URL or defaults
  const activeTab =
    tabId && validTabs.includes(tabId)
      ? (tabId as
          | "teams"
          | "members"
          | "integrations"
          | "settings"
          | "activity"
          | "billing")
      : isPersonal
        ? "settings"
        : "members";

  const setActiveTab = (tab: string) => {
    navigate(`/${currentOrg.slug}/workspace/${tab}`);
  };

  const [isLoading, setIsLoading] = useState(false);

  // Role Modal State
  const [roleModal, setRoleModal] = useState<{
    isOpen: boolean;
    type: "WORKSPACE" | "TEAM";
    userId: string;
    currentRole: string;
    targetId?: string; // teamId if type is TEAM
  }>({ isOpen: false, type: "WORKSPACE", userId: "", currentRole: "" });

  // Teams State
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [deleteConfirmTeamId, setDeleteConfirmTeamId] = useState<string | null>(
    null,
  );
  const [isAddingMember, setIsAddingMember] = useState(false); // Adding to Team
  const [memberSearch, setMemberSearch] = useState("");

  // Members State (Invite to Workspace)
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState("");

  const filteredWorkspaceUsers = React.useMemo(() => {
    return users
      .filter(
        (u) =>
          u.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
          u.email.toLowerCase().includes(userSearchQuery.toLowerCase()),
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users, userSearchQuery]);

  // Remove Confirmation
  const [removeConfirm, setRemoveConfirm] = useState<{
    isOpen: boolean;
    type: "WORKSPACE" | "TEAM";
    userId: string;
    userName: string;
    targetId?: string;
  }>({ isOpen: false, type: "WORKSPACE", userId: "", userName: "" });

  // Org Settings
  const [renameValue, setRenameValue] = useState(currentOrg.name);
  const [deleteOrgConfirm, setDeleteOrgConfirm] = useState(false);
  const [leaveOrgConfirm, setLeaveOrgConfirm] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [githubModalOpen, setGithubModalOpen] = useState(false);

  useEffect(() => {
    setRenameValue(currentOrg.name);
  }, [currentOrg.name]);

  useEffect(() => {
    // Tab Redirection Logic
    if (activeTab === "billing" && !isMaintainer) {
      navigate(`/${currentOrg.slug}/workspace/members`, { replace: true });
    }
  }, [
    activeTab,
    isPersonal,
    currentOrg.tier,
    isMaintainer,
    currentOrg.slug,
    navigate,
  ]);

  const initRef = useRef(false);

  const fetchActivity = useCallback(
    async (offset = 0, append = false) => {
      const key = `activity-${currentOrg.id}-${activityAction}-${offset}-${append}`;
      if (activePromises.current[key]) return activePromises.current[key];

      activePromises.current[key] = (async () => {
        setIsActivityLoading(true);
        try {
          const token = await getToken();
          const data = await getActivityLogs(
            currentOrg.id,
            token || undefined,
            {
              offset,
              limit: 10,
              action: activityAction,
            },
          );
          if (data && data.logs) {
            if (append) {
              setActivityLogs((prev) => [...prev, ...data.logs]);
            } else {
              setActivityLogs(data.logs);
            }
            setActivityTotal(data.total || 0);
            setHasMoreActivity(offset + data.logs.length < (data.total || 0));
          } else {
            if (!append) setActivityLogs([]);
            setActivityTotal(0);
            setHasMoreActivity(false);
          }
          setActivityOffset(offset);
          setLoadedTabs((prev) => new Set(prev).add("activity"));
        } catch (e) {
          console.error("Failed to fetch activity logs", e);
        } finally {
          setIsActivityLoading(false);
          delete activePromises.current[key];
        }
      })();
      return activePromises.current[key];
    },
    [currentOrg.id, getToken, activityAction],
  );

  const fetchMembersData = useCallback(async () => {
    const key = `members-${currentOrg.id}`;
    if (activePromises.current[key]) return activePromises.current[key];

    activePromises.current[key] = (async () => {
      setIsLoading(true);
      try {
        const token = await getToken();
        const [fetchedUsers, fetchedInvites] = await Promise.all([
          getUsers(currentOrg.id, token || undefined),
          getOrgInvitations(currentOrg.id, token || undefined),
        ]);
        setUsers(fetchedUsers);
        setInvitations(fetchedInvites);
        setLoadedTabs((prev) => new Set(prev).add("members"));
      } catch (e) {
        setErrorMessage("Failed to load members data");
      } finally {
        setIsLoading(false);
        delete activePromises.current[key];
      }
    })();
    return activePromises.current[key];
  }, [currentOrg.id, getToken]);

  const fetchTeamsData = useCallback(async () => {
    const key = `teams-${currentOrg.id}`;
    if (activePromises.current[key]) return activePromises.current[key];

    activePromises.current[key] = (async () => {
      setIsLoading(true);
      try {
        const token = await getToken();
        // Teams tab needs both teams AND users to show member details
        const [fetchedTeams] = await Promise.all([
          getTeams(currentOrg.id, token || undefined),
          fetchMembersData(), // This will be guarded by its own promise key
        ]);
        setTeams(fetchedTeams);
        setLoadedTabs((prev) => new Set(prev).add("teams"));
      } catch (e) {
        setErrorMessage("Failed to load teams data");
      } finally {
        setIsLoading(false);
        delete activePromises.current[key];
      }
    })();
    return activePromises.current[key];
  }, [currentOrg.id, getToken, fetchMembersData]);

  const fetchData = useCallback(async () => {
    if (activeTab === "members") {
      await fetchMembersData();
    } else if (activeTab === "teams") {
      await fetchTeamsData();
    } else if (activeTab === "activity") {
      await fetchActivity(0, false);
    }
  }, [activeTab, fetchMembersData, fetchTeamsData, fetchActivity]);

  const lastFetchedTab = useRef<string | null>(null);
  const lastActivityAction = useRef(activityAction);

  useEffect(() => {
    const isTabNotLoaded = !loadedTabs.has(activeTab);
    const tabChanged = lastFetchedTab.current !== activeTab;
    const actionChanged = activityAction !== lastActivityAction.current;

    if (activeTab === "activity") {
      if (isTabNotLoaded || actionChanged || tabChanged) {
        lastFetchedTab.current = activeTab;
        lastActivityAction.current = activityAction;
        fetchActivity(0, false);
      }
    } else if (isTabNotLoaded || tabChanged) {
      lastFetchedTab.current = activeTab;
      fetchData();
    }
  }, [activeTab, activityAction, fetchData, fetchActivity, loadedTabs]);

  const refreshData = async () => {
    if (activeTab === "members") {
      await fetchMembersData();
    } else if (activeTab === "teams") {
      await fetchTeamsData();
    } else if (activeTab === "activity") {
      await fetchActivity(0, false);
    }
  };

  // --- Org Management ---

  const handleRenameOrg = async () => {
    if (!renameValue.trim()) return;
    try {
      const token = await getToken();
      await renameOrganisation(currentOrg.id, renameValue, token || undefined);
      window.location.reload();
    } catch (e) {
      setErrorMessage("Failed to rename workspace: " + e);
    }
  };

  const handleDeleteOrg = async () => {
    try {
      const token = await getToken();
      await deleteOrganisation(currentOrg.id, token || undefined);
      window.location.href = "/dashboard";
    } catch (e) {
      setErrorMessage("Failed to delete workspace: " + e);
    }
  };

  const handleLeaveOrg = async () => {
    try {
      const token = await getToken();
      await leaveOrganisation(currentOrg.id, token || undefined);
      window.location.href = "/dashboard";
    } catch (e) {
      setErrorMessage("Failed to leave workspace: " + e);
    }
  };

  const handleUpdateOrgMemberRole = async (userId: string, role: string) => {
    try {
      const token = await getToken();
      await updateMemberRole(currentOrg.id, userId, role, token || undefined);
      await refreshData();
    } catch (e) {
      setErrorMessage("Failed to update role: " + e);
    }
  };

  const handleRemoveWorkspaceMember = async (userId: string) => {
    try {
      const token = await getToken();
      await removeMember(currentOrg.id, userId, token || undefined);
      await refreshData();
    } catch (e) {
      setErrorMessage("Failed to remove member: " + e);
    }
  };

  // --- Team Management ---

  const handleCreateTeam = async () => {
    if (isReadOnly) return;
    if (!newTeamName.trim()) return;
    const token = await getToken();
    const newTeam: Team = {
      id: generateId(),
      organisationId: currentOrg.id,
      name: newTeamName,
      members: [],
    };
    await saveTeam(newTeam, token || undefined);
    await refreshData();
    setNewTeamName("");
    setIsCreatingTeam(false);
    setSelectedTeamId(newTeam.id);
  };

  const handleDeleteTeam = (id: string) => {
    if (!isMaintainer) return;
    setDeleteConfirmTeamId(id);
  };

  const confirmDeleteTeam = async () => {
    if (!deleteConfirmTeamId) return;
    try {
      const token = await getToken();
      await deleteTeam(deleteConfirmTeamId, token || undefined);
      setTeams((prev) => prev.filter((t) => t.id !== deleteConfirmTeamId));
      if (selectedTeamId === deleteConfirmTeamId) setSelectedTeamId(null);
    } catch (e) {
      setErrorMessage(String(e));
    }
  };

  const handleUpdateTeamMember = async (userId: string, role: string) => {
    const team = teams.find(
      (t) => t.id === (roleModal.targetId || selectedTeamId),
    );
    if (!team) return;

    const existingMember = team.members.find((m) => m.userId === userId);
    let newMembers = [...team.members];

    if (existingMember) {
      newMembers = newMembers.map((m) =>
        m.userId === userId ? { ...m, role } : m,
      );
    } else {
      newMembers.push({ userId, role });
    }

    const token = await getToken();
    const updatedTeam = {
      ...team,
      members: newMembers,
      memberIds: newMembers.map((m) => m.userId),
    };

    try {
      await saveTeam(updatedTeam, token || undefined);
      await refreshData();
      if (!existingMember) setIsAddingMember(false);
    } catch (e) {
      setErrorMessage(String(e));
    }
  };

  const handleRemoveTeamMember = async (teamId: string, userId: string) => {
    const team = teams.find((t) => t.id === teamId);
    if (!team) return;

    const token = await getToken();
    const updatedTeam = {
      ...team,
      members: team.members.filter((m) => m.userId !== userId),
    };
    updatedTeam.memberIds = updatedTeam.members.map((m) => m.userId);

    try {
      await saveTeam(updatedTeam, token || undefined);
      await refreshData();
    } catch (e) {
      setErrorMessage(String(e));
    }
  };

  const handleInviteByEmail = async (email: string) => {
    if (isReadOnly) return;
    try {
      const token = await getToken();
      await inviteUser(currentOrg.id, email, "MEMBER", token || undefined);
      await refreshData();
      setSuccessMessage(
        `An invitation has been sent to ${email}. They will receive an email with instructions on how to join.`,
      );
    } catch (e) {
      setErrorMessage("Failed to send invite: " + e);
    }
  };

  const handleCancelInvite = async (id: string) => {
    try {
      const token = await getToken();
      await cancelInvitation(id, token || undefined);
      setInvitations((prev) => prev.filter((i) => i.id !== id));
    } catch (e) {
      setErrorMessage("Failed to cancel invitation: " + e);
    }
  };

  const selectedTeam = teams.find((t) => t.id === selectedTeamId);

  const filteredUsersForTeam = users.filter(
    (u) =>
      selectedTeam &&
      !selectedTeam.memberIds.includes(u.id) &&
      (u.name.toLowerCase().includes(memberSearch.toLowerCase()) ||
        u.email.includes(memberSearch)),
  );

  const workspaceRoles = [
    {
      value: "MAINTAINER",
      label: "Admin",
      hint: "Complete control over the workspace. Can manage teams, members, and all plans/runs.",
    },
    {
      value: "MEMBER",
      label: "Member",
      hint: "Can be assigned to teams or tasks in a taskplan.",
    },
  ];

  const teamRoles = [
    {
      value: "MAINTAINER",
      label: "Maintainer",
      hint: "Can manage team members and their roles within this team.",
    },
    {
      value: "MEMBER",
      label: "Member",
      hint: "Regular member of the team. Can complete tasks assigned to the team.",
    },
  ];

  const getActivityIcon = (action: string) => {
    switch (action) {
      case "PLAN_CREATED":
        return <Plus className="text-green-500" size={14} />;
      case "PLAN_UPDATED":
        return <Settings2 className="text-blue-500" size={14} />;
      case "PLAN_DELETED":
        return <Trash2 className="text-red-500" size={14} />;
      case "RUN_STARTED":
        return <Zap className="text-indigo-500" size={14} />;
      case "RUN_COMPLETED":
        return <CheckCircle size={14} className="text-green-500" />;
      case "TASK_COMPLETED":
        return <CheckCircle size={14} className="text-emerald-500" />;
      case "TASK_SKIPPED":
        return <XCircle size={14} className="text-slate-400" />;
      case "TASK_RETRIED":
        return <RotateCw size={14} className="text-amber-500" />;
      case "MEMBER_INVITED":
        return <Mail className="text-blue-500" size={14} />;
      case "MEMBER_JOINED":
        return <Users className="text-green-500" size={14} />;
      case "MEMBER_REMOVED":
        return <Trash2 className="text-red-500" size={14} />;
      case "TEAM_CREATED":
        return <Users className="text-green-500" size={14} />;
      case "TEAM_UPDATED":
        return <Settings2 className="text-blue-500" size={14} />;
      case "TEAM_DELETED":
        return <Trash2 className="text-red-500" size={14} />;
      default:
        return <History className="text-slate-400" size={14} />;
    }
  };

  const getActivityMessage = (log: any) => {
    const { action, details, user } = log;
    const userName = user?.name || "System";

    switch (action) {
      case "PLAN_CREATED":
        return (
          <>
            <span className="font-bold">{userName}</span> created plan{" "}
            <span className="font-bold">"{details.name}"</span>
          </>
        );
      case "PLAN_UPDATED":
        return (
          <>
            <span className="font-bold">{userName}</span> updated plan{" "}
            <span className="font-bold">"{details.name}"</span>
          </>
        );
      case "PLAN_DELETED":
        return (
          <>
            <span className="font-bold">{userName}</span> deleted plan{" "}
            <span className="font-bold">"{details.name}"</span>
          </>
        );
      case "RUN_STARTED":
        return (
          <>
            <span className="font-bold">{userName}</span> started execution{" "}
            <span className="font-bold">"{details.name}"</span>
          </>
        );
      case "RUN_COMPLETED":
        return (
          <>
            Execution <span className="font-bold">"{details.name}"</span>{" "}
            completed successfully
          </>
        );
      case "TASK_COMPLETED":
        return (
          <>
            <span className="font-bold">{userName}</span> completed task{" "}
            <span className="font-bold">"{details.taskName}"</span> in{" "}
            {details.runName}
          </>
        );
      case "TASK_SKIPPED":
        return (
          <>
            <span className="font-bold">{userName}</span> skipped task{" "}
            <span className="font-bold">"{details.taskName}"</span> in{" "}
            {details.runName}
          </>
        );
      case "TASK_RETRIED":
        return (
          <>
            <span className="font-bold">{userName}</span> retried task{" "}
            <span className="font-bold">"{details.taskName}"</span> in{" "}
            {details.runName}
          </>
        );
      case "MEMBER_INVITED":
        return (
          <>
            <span className="font-bold">{userName}</span> invited{" "}
            <span className="font-bold">{details.email}</span> to the workspace
          </>
        );
      case "MEMBER_JOINED":
        return (
          <>
            <span className="font-bold">{details.email}</span> joined the
            workspace
          </>
        );
      case "MEMBER_REMOVED":
        return (
          <>
            <span className="font-bold">{userName}</span> removed a member from
            the workspace
          </>
        );
      case "TEAM_CREATED":
        return (
          <>
            <span className="font-bold">{userName}</span> created team{" "}
            <span className="font-bold">"{details.name}"</span>
          </>
        );
      case "TEAM_UPDATED":
        return (
          <>
            <span className="font-bold">{userName}</span> updated team{" "}
            <span className="font-bold">"{details.name}"</span>
          </>
        );
      case "TEAM_DELETED":
        return (
          <>
            <span className="font-bold">{userName}</span> deleted team{" "}
            <span className="font-bold">"{details.name}"</span>
          </>
        );
      default:
        return `${userName} performed ${action}`;
    }
  };

  return (
    <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 h-full flex flex-col">
      <ConfirmationModal
        isOpen={!!deleteConfirmTeamId}
        onClose={() => setDeleteConfirmTeamId(null)}
        onConfirm={confirmDeleteTeam}
        title="Delete Team"
        message="Are you sure you want to delete this team? This cannot be undone."
        confirmText="Delete Team"
        confirmVariant="danger"
      />
      <ConfirmationModal
        isOpen={deleteOrgConfirm}
        onClose={() => setDeleteOrgConfirm(false)}
        onConfirm={handleDeleteOrg}
        title="Delete Workspace"
        message={`Are you sure you want to delete "${currentOrg.name}"? This will permanently delete all plans, runs, and data associated with this workspace. This action cannot be undone.`}
        confirmText="Delete Workspace"
        confirmVariant="danger"
      />
      <ConfirmationModal
        isOpen={leaveOrgConfirm}
        onClose={() => setLeaveOrgConfirm(false)}
        onConfirm={handleLeaveOrg}
        title="Leave Workspace"
        message={`Are you sure you want to leave "${currentOrg.name}"? You will lose access to all plans and teams in this workspace.`}
        confirmText="Leave Workspace"
        confirmVariant="danger"
      />
      <ConfirmationModal
        isOpen={removeConfirm.isOpen}
        onClose={() => setRemoveConfirm({ ...removeConfirm, isOpen: false })}
        onConfirm={() => {
          if (removeConfirm.type === "WORKSPACE") {
            handleRemoveWorkspaceMember(removeConfirm.userId);
          } else if (removeConfirm.targetId) {
            handleRemoveTeamMember(
              removeConfirm.targetId,
              removeConfirm.userId,
            );
          }
        }}
        title={
          removeConfirm.type === "WORKSPACE"
            ? "Remove from Workspace"
            : "Remove from Team"
        }
        message={`Are you sure you want to remove ${removeConfirm.userName} from this ${removeConfirm.type === "WORKSPACE" ? "workspace" : "team"}?`}
        confirmText="Remove"
        confirmVariant="danger"
      />
      <RoleModal
        isOpen={roleModal.isOpen}
        onClose={() => setRoleModal({ ...roleModal, isOpen: false })}
        onConfirm={(role) => {
          if (roleModal.type === "WORKSPACE") {
            handleUpdateOrgMemberRole(roleModal.userId, role);
          } else {
            handleUpdateTeamMember(roleModal.userId, role);
          }
        }}
        title={
          roleModal.type === "WORKSPACE"
            ? "Change Workspace Role"
            : "Change Team Role"
        }
        currentRole={roleModal.currentRole}
        roles={roleModal.type === "WORKSPACE" ? workspaceRoles : teamRoles}
      />

      <GithubInstallModal
        isOpen={githubModalOpen}
        onClose={() => setGithubModalOpen(false)}
        orgId={currentOrg.id}
        githubInstallationId={currentOrg.githubInstallationId}
      />
      <InviteModal
        isOpen={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onConfirm={handleInviteByEmail}
      />
      <ErrorModal
        isOpen={!!errorMessage}
        onClose={() => setErrorMessage(null)}
        message={errorMessage || ""}
        title="Error"
      />
      <SuccessModal
        isOpen={!!successMessage}
        onClose={() => setSuccessMessage(null)}
        message={successMessage || ""}
        title="Invitation Sent"
      />

      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 group">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
              {currentOrg.name}
            </h1>
          </div>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Manage workspace settings, members, and teams.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-slate-200 dark:border-slate-700 mb-6 overflow-x-auto">
        <button
          onClick={() => setActiveTab("members")}
          data-testid="tab-users"
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
            activeTab === "members"
              ? "border-indigo-600 text-indigo-600 dark:text-indigo-400"
              : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          }`}
        >
          Users{" "}
          {isPersonal && (
            <div className="p-0.5 bg-indigo-100 text-indigo-600 rounded-md">
              <Crown size={12} fill="currentColor" />
            </div>
          )}
        </button>
        <button
          onClick={() => setActiveTab("teams")}
          data-testid="tab-teams"
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
            activeTab === "teams"
              ? "border-indigo-600 text-indigo-600 dark:text-indigo-400"
              : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          }`}
        >
          Teams{" "}
          {isPersonal && (
            <div className="p-0.5 bg-indigo-100 text-indigo-600 rounded-md">
              <Crown size={12} fill="currentColor" />
            </div>
          )}
        </button>
        <button
          onClick={() => setActiveTab("integrations")}
          data-testid="tab-integrations"
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
            activeTab === "integrations"
              ? "border-indigo-600 text-indigo-600 dark:text-indigo-400"
              : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          }`}
        >
          Integrations{" "}
          {isPersonal && (
            <div className="p-0.5 bg-indigo-100 text-indigo-600 rounded-md">
              <Crown size={12} fill="currentColor" />
            </div>
          )}
        </button>

        {isMaintainer && (
          <button
            onClick={() => setActiveTab("activity")}
            data-testid="tab-activity"
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === "activity"
                ? "border-indigo-600 text-indigo-600 dark:text-indigo-400"
                : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            }`}
          >
            Activity
            {currentOrg.tier !== "ENTERPRISE" && (
              <div className="p-0.5 bg-indigo-100 text-indigo-600 rounded-md">
                <Crown size={12} fill="currentColor" />
              </div>
            )}
          </button>
        )}

        {/* In personal workspace, activity is hidden or also upsold? Let's hide activity for simplicity unless requested, but user said "hide most of the tabs" implies showing them. Let's just stick to the main ones first. */}

        {isMaintainer && (
          <button
            onClick={() => setActiveTab("billing")}
            data-testid="tab-billing"
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === "billing"
                ? "border-indigo-600 text-indigo-600 dark:text-indigo-400"
                : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            }`}
          >
            Billing
          </button>
        )}
        <button
          onClick={() => setActiveTab("settings")}
          data-testid="tab-settings"
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === "settings"
              ? "border-indigo-600 text-indigo-600 dark:text-indigo-400"
              : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          }`}
        >
          Settings
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 pt-4">
        {isPersonal && (
          <div className="mb-8">
            <UpgradePrompt
              currentOrg={currentOrg}
              title="Ready for more?"
              message="Personal workspaces are great for individual projects. To collaborate with others, create teams, and access advanced features, you'll need a shared workspace."
              upgradeLink="Create shared workspace"
              testId="create-shared-workspace-cta"
              onUpgrade={() => {
                onOpenCreateWorkspace();
              }}
            />
            {/* <UpgradePrompt
              currentOrg={currentOrg}
              title="Ready to collaborate?"
              message="Upgrade to a Team plan to create shared workspaces, invite your colleagues, and use advanced GitHub automations."
              upgradeLink="Create shared workspace"
              onUpgrade={() => {
                onOpenCreateWorkspace();
              }}
            /> */}
          </div>
        )}

        {activeTab === "billing" && isMaintainer ? (
          <BillingTab currentOrg={currentOrg} />
        ) : activeTab === "settings" ? (
          <div className="max-w-2xl space-y-8">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <Settings2
                  size={20}
                  className="text-indigo-600 dark:text-indigo-400"
                />
                General Settings
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Workspace Name
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      readOnly={!isMaintainer}
                      className={`flex-1 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white rounded-md shadow-sm p-2.5 text-sm ${!isMaintainer ? "opacity-70 cursor-not-allowed" : ""}`}
                      data-testid="workspace-name-input"
                    />
                    {isMaintainer && (
                      <button
                        onClick={handleRenameOrg}
                        disabled={
                          renameValue === currentOrg.name || !renameValue.trim()
                        }
                        data-testid="save-workspace-name-button"
                        className="px-4 py-2 bg-indigo-600 disabled:opacity-50 disabled:hover:bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 transition"
                      >
                        Save
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Workspace URL
                  </label>
                  <div className="flex items-center border border-slate-200 dark:border-slate-700 rounded-md bg-slate-50 dark:bg-slate-900/50 px-3 py-2.5">
                    <span className="text-slate-500 dark:text-slate-400 text-sm flex-1">
                      taskplan.run/{currentOrg.slug}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    The workspace URL cannot be changed.
                  </p>
                </div>
              </div>
            </div>

            {!isPersonal && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-red-200 dark:border-red-900/30 p-6 shadow-sm">
                <h2 className="text-lg font-bold text-red-600 dark:text-red-400 mb-2 flex items-center gap-2">
                  <ShieldAlert size={20} />
                  Danger Zone
                </h2>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  {isOwner
                    ? "Deleting a workspace will permanently remove all plans, execution history, and team configurations. This action cannot be undone."
                    : "Leaving this workspace will remove your access to all its plans and teams. You will need to be re-invited to join again."}
                </p>
                {isOwner ? (
                  <button
                    onClick={() => setDeleteOrgConfirm(true)}
                    data-testid="delete-workspace-button"
                    className="px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/30 rounded-md text-sm font-medium hover:bg-red-100 dark:hover:bg-red-900/40 transition"
                  >
                    Delete Workspace
                  </button>
                ) : (
                  <button
                    onClick={() => setLeaveOrgConfirm(true)}
                    data-testid="leave-workspace-button"
                    className="px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/30 rounded-md text-sm font-medium hover:bg-red-100 dark:hover:bg-red-900/40 transition flex items-center gap-2"
                  >
                    <LogOut size={16} /> Leave Workspace
                  </button>
                )}
              </div>
            )}
          </div>
        ) : activeTab === "integrations" ? (
          isPersonal ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm text-center px-6">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mb-6">
                <Github
                  size={32}
                  className="text-slate-400 dark:text-slate-500"
                />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
                GitHub Integrations
              </h2>
              <p className="text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-8 text-lg">
                Connect your GitHub repositories and trigger workflows directly
                from Taskplan. Available on Team and Enterprise plans.
              </p>
              <button
                onClick={onOpenCreateWorkspace}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition shadow-lg shadow-indigo-500/20"
              >
                Upgrade to Team Plan
              </button>
            </div>
          ) : (
            <div className="max-w-2xl space-y-8">
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <Github
                    size={20}
                    className="text-indigo-600 dark:text-indigo-400"
                  />
                  GitHub Integration
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800">
                    <div className="p-2 bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                      <Github
                        size={24}
                        className="text-slate-900 dark:text-white"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-bold text-slate-900 dark:text-white">
                          GitHub Actions Automation
                        </h3>
                        {currentOrg.githubInstallationId ? (
                          <span className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-[10px] font-bold uppercase tracking-wider rounded-full">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            Connected
                          </span>
                        ) : (
                          <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-wider rounded-full border border-slate-200 dark:border-slate-700">
                            Not Connected
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed mb-4">
                        Run GitHub Actions workflows directly from your
                        taskplans. Connect your GitHub organisation to give
                        Taskplan permission to trigger workflows.
                      </p>

                      {isMaintainer ? (
                        <button
                          onClick={() => {
                            setGithubModalOpen(true);
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition shadow-sm"
                        >
                          {currentOrg.githubInstallationId
                            ? "Manage Installation"
                            : "Install GitHub App"}
                        </button>
                      ) : (
                        <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                          Only workspace maintainers can configure GitHub
                          integration.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        ) : activeTab === "teams" ? (
          isPersonal ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm text-center px-6">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mb-6">
                <Users
                  size={32}
                  className="text-slate-400 dark:text-slate-500"
                />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
                Team Management
              </h2>
              <p className="text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-8 text-lg">
                Organize your members into teams for easier assignment and
                management. Available on Team and Enterprise plans.
              </p>
              <button
                onClick={onOpenCreateWorkspace}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition shadow-lg shadow-indigo-500/20"
              >
                Upgrade to Team Plan
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-8 min-h-full">
              <div className="col-span-12 md:col-span-3 flex flex-col bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-900/50">
                  <h2 className="font-bold text-slate-800 dark:text-white flex items-center gap-2 text-sm">
                    <Users
                      size={16}
                      className="text-indigo-600 dark:text-indigo-400"
                    />
                    Teams
                  </h2>
                  {isMaintainer && (
                    <button
                      data-testid="add-team-button"
                      disabled={isReadOnly}
                      onClick={() => {
                        setIsCreatingTeam(true);
                        setSelectedTeamId(null);
                      }}
                      className={`p-1.5 rounded transition ${isReadOnly ? "text-slate-300 dark:text-slate-600 cursor-not-allowed" : "hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400"}`}
                    >
                      <Plus size={16} />
                    </button>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1 relative min-h-[200px]">
                  {isLoading && !loadedTabs.has("teams") ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 gap-3 bg-white/50 dark:bg-slate-800/50 backdrop-blur-[1px] z-10">
                      <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                      <p className="text-xs font-medium">Loading teams...</p>
                    </div>
                  ) : null}
                  {teams.length === 0 && !isLoading ? (
                    <div className="p-4 text-xs text-slate-400 text-center italic">
                      No teams yet.
                    </div>
                  ) : (
                    teams.map((team) => (
                      <button
                        key={team.id}
                        onClick={() => {
                          setSelectedTeamId(team.id);
                          setIsCreatingTeam(false);
                        }}
                        className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition flex justify-between group items-center ${selectedTeamId === team.id ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300" : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700"}`}
                      >
                        <span className="truncate">{team.name}</span>
                        {isMaintainer && (
                          <span
                            className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 p-1 hover:bg-white dark:hover:bg-slate-800 rounded"
                            data-testid="delete-team-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTeam(team.id);
                            }}
                          >
                            <Trash2 size={12} />
                          </span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>

              <div className="col-span-12 md:col-span-9 h-full overflow-hidden">
                {isCreatingTeam ? (
                  <div className="bg-white dark:bg-slate-800 p-8 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 max-w-lg mx-auto mt-10">
                    <h2 className="text-xl font-bold text-slate-800 dark:text-white mb-6">
                      Create New Team
                    </h2>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Team Name
                        </label>
                        <input
                          type="text"
                          value={newTeamName}
                          maxLength={100}
                          onChange={(e) => setNewTeamName(e.target.value)}
                          placeholder="e.g. Engineering"
                          data-testid="team-name-input"
                          className="w-full border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-md shadow-sm p-2.5 focus:ring-indigo-500 focus:border-indigo-500"
                          autoFocus
                        />
                      </div>
                      <div className="flex gap-3 justify-end">
                        <button
                          onClick={() => {
                            setIsCreatingTeam(false);
                            setSelectedTeamId(teams[0]?.id || null);
                          }}
                          className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleCreateTeam}
                          data-testid="create-team-submit"
                          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm font-medium"
                        >
                          Create Team
                        </button>
                      </div>
                    </div>
                  </div>
                ) : selectedTeam ? (
                  <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col h-full overflow-hidden">
                    <div className="px-8 py-6 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
                      <div>
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                          {selectedTeam.name}
                        </h1>
                        <p className="text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-2 text-sm">
                          <span className="w-2 h-2 rounded-full bg-green-500"></span>
                          {selectedTeam.memberIds.length} members
                        </p>
                      </div>
                      {isMaintainer && (
                        <button
                          onClick={() => setIsAddingMember(true)}
                          className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 shadow-sm transition font-medium"
                        >
                          <Plus size={18} /> Add member
                        </button>
                      )}
                    </div>

                    {isAddingMember && isMaintainer && (
                      <div className="p-6 bg-indigo-50/50 dark:bg-indigo-900/10 border-b border-indigo-100 dark:border-indigo-900/30">
                        <div className="flex justify-between items-start mb-4">
                          <h3 className="font-bold text-indigo-900 dark:text-indigo-200">
                            Add people to {selectedTeam.name}
                          </h3>
                          <button
                            onClick={() => setIsAddingMember(false)}
                            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                          >
                            <XCircle size={18} />
                          </button>
                        </div>
                        <div className="relative mb-2">
                          <Search
                            className="absolute left-3 top-2.5 text-slate-400"
                            size={14}
                          />
                          <input
                            type="text"
                            value={memberSearch}
                            onChange={(e) => setMemberSearch(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 text-sm border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 rounded-md"
                            placeholder="Search workspace users..."
                          />
                        </div>
                        <div className="space-y-2 max-h-48 overflow-y-auto bg-white dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700 p-1">
                          {filteredUsersForTeam.map((user) => (
                            <button
                              key={user.id}
                              onClick={() =>
                                handleUpdateTeamMember(user.id, "MEMBER")
                              }
                              className="w-full flex items-center gap-3 p-2 hover:bg-slate-50 dark:hover:bg-slate-700 rounded text-left transition"
                            >
                              <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] text-white font-bold bg-indigo-500">
                                {user.initials}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-slate-800 dark:text-white truncate">
                                  {user.name}
                                </div>
                                <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                  {user.email}
                                </div>
                              </div>
                              <Plus
                                size={14}
                                className="text-indigo-600 dark:text-indigo-400"
                              />
                            </button>
                          ))}
                          {filteredUsersForTeam.length === 0 && (
                            <div className="text-xs text-slate-400 text-center py-4">
                              No matching users found or all are already added.
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex-1 overflow-y-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700 sticky top-0">
                          <tr>
                            <th className="text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider py-3 px-8">
                              User
                            </th>
                            <th className="text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider py-3 px-8">
                              Email
                            </th>
                            <th className="text-right text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider py-3 px-8"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                          {selectedTeam.memberIds.map((userId) => {
                            const user = users.find((u) => u.id === userId);
                            const member = selectedTeam.members.find(
                              (m) => m.userId === userId,
                            );
                            if (!user) return null;

                            const currentTeamRole = member?.role || "MEMBER";
                            const isUserTeamMaintainer =
                              currentTeamRole === "MAINTAINER";

                            const canManageTeam =
                              isMaintainer ||
                              selectedTeam.members.find(
                                (m) => m.userId === currentUser.id,
                              )?.role === "MAINTAINER";

                            return (
                              <tr
                                key={userId}
                                className="group hover:bg-slate-50 dark:hover:bg-slate-700/30 transition"
                              >
                                <td className="py-4 px-8 flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs text-white font-bold overflow-hidden bg-indigo-500">
                                    {user.imageUrl ? (
                                      <img
                                        src={user.imageUrl}
                                        alt={user.name}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      user.initials
                                    )}
                                  </div>
                                  <div className="flex flex-col">
                                    <div className="text-sm font-medium text-slate-800 dark:text-white text-nowrap flex items-center gap-2">
                                      {user.name}
                                      {isUserTeamMaintainer && (
                                        <span className="text-[10px] bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">
                                          Maintainer
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="py-4 px-8 text-sm text-slate-500 dark:text-slate-400">
                                  {user.email}
                                </td>
                                <td className="py-4 px-8 text-right">
                                  {canManageTeam && (
                                    <div className="flex justify-end gap-2">
                                      <button
                                        onClick={() =>
                                          setRoleModal({
                                            isOpen: true,
                                            type: "TEAM",
                                            userId: user.id,
                                            currentRole: currentTeamRole,
                                            targetId: selectedTeam.id,
                                          })
                                        }
                                        className="flex items-center gap-1.5 text-xs text-nowrap font-medium text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 rounded-lg shadow-sm transition"
                                      >
                                        <Settings2 size={14} /> Change role
                                      </button>
                                      <button
                                        onClick={() =>
                                          setRemoveConfirm({
                                            isOpen: true,
                                            type: "TEAM",
                                            userId: user.id,
                                            userName: user.name,
                                            targetId: selectedTeam.id,
                                          })
                                        }
                                        className="flex items-center gap-1.5 text-xs text-nowrap font-medium text-slate-400 hover:text-red-600 dark:hover:text-red-400 px-2.5 py-1.5 transition"
                                      >
                                        <Trash2 size={14} /> Remove
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          {selectedTeam.memberIds.length === 0 && (
                            <tr>
                              <td
                                colSpan={3}
                                className="py-12 text-center text-slate-400 italic"
                              >
                                No members in this team.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-slate-400 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 border-dashed">
                    <Users
                      size={48}
                      className="mb-4 text-slate-200 dark:text-slate-700"
                    />
                    <p className="font-medium">
                      Select a team to manage members
                    </p>
                    {isMaintainer && (
                      <button
                        onClick={() => setIsCreatingTeam(true)}
                        disabled={isReadOnly}
                        data-testid="create-team-empty-button"
                        className={`mt-4 font-medium hover:underline ${isReadOnly ? "text-slate-400 cursor-not-allowed no-underline" : "text-indigo-600 dark:text-indigo-400"}`}
                      >
                        Or create a new team
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        ) : activeTab === "members" ? (
          isPersonal ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm text-center px-6">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mb-6">
                <Shield
                  size={32}
                  className="text-slate-400 dark:text-slate-500"
                />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
                Invite Members
              </h2>
              <p className="text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-8 text-lg">
                Collaboration is key. Invite your colleagues to this workspace
                to share plans and execute runs together. Available on Team and
                Enterprise plans.
              </p>
              <button
                onClick={onOpenCreateWorkspace}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition shadow-lg shadow-indigo-500/20"
              >
                Upgrade to Team Plan
              </button>
            </div>
          ) : (
            <div className="flex flex-col min-h-full bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
              <div className="p-6 border-b border-slate-100 dark:border-slate-700 flex justify-between items-end bg-slate-50 dark:bg-slate-900/50">
                <div>
                  <h2 className="font-bold text-slate-800 dark:text-white flex items-center gap-2">
                    <Shield
                      size={20}
                      className="text-indigo-600 dark:text-indigo-400"
                    />
                    Users
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    Manage access to {currentOrg.name}.
                  </p>
                </div>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search
                      className="absolute left-3 top-2.5 text-slate-400"
                      size={14}
                    />
                    <input
                      type="text"
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      placeholder="Search users..."
                      className="text-sm border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-md pl-9 pr-3 py-2 w-48 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    />
                  </div>
                  {isMaintainer && (
                    <button
                      data-testid="invite-user-button"
                      disabled={isReadOnly}
                      onClick={() => setInviteModalOpen(true)}
                      className={`flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-nowrap rounded-md text-sm font-medium transition shadow-sm ${isReadOnly ? "opacity-50 cursor-not-allowed" : "hover:bg-indigo-700"}`}
                    >
                      <Plus size={16} /> Invite
                    </button>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto relative">
                {/* Membership Limit Up-sell */}
                {currentOrg.subscriptionStatus === "FREE" &&
                  users.length >= 1 && (
                    <div className="mb-6">
                      <UpgradePrompt
                        currentOrg={currentOrg}
                        title="Membership Limit Reached"
                        message="Free workspaces are limited to 1 member (the owner). Upgrade to a Team plan to invite your colleagues and collaborate."
                        onUpgrade={() =>
                          navigate(`/${currentOrg.slug}/workspace/billing`)
                        }
                      />
                    </div>
                  )}
                {isLoading && !loadedTabs.has("members") ? (
                  <div className="py-20 flex flex-col items-center justify-center text-slate-400 gap-3">
                    <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-sm font-medium">
                      Loading workspace members...
                    </p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700 sticky top-0">
                      <tr>
                        <th className="text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider py-3 px-8">
                          User
                        </th>
                        <th className="text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider py-3 px-8">
                          Email
                        </th>
                        <th className="text-right text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider py-3 px-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                      {filteredWorkspaceUsers.map((user) => {
                        const currentWorkspaceRole = user.role || "MEMBER";
                        const isUserAdmin =
                          currentWorkspaceRole === "MAINTAINER";

                        return (
                          <tr
                            key={user.id}
                            className="group hover:bg-slate-50 dark:hover:bg-slate-700/30 transition"
                          >
                            <td className="py-4 px-8 flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs text-white font-bold overflow-hidden bg-indigo-500">
                                {user.imageUrl ? (
                                  <img
                                    src={user.imageUrl}
                                    alt={user.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  user.initials
                                )}
                              </div>
                              <div className="flex flex-col">
                                <div className="text-sm font-medium text-slate-800 dark:text-white text-nowrap flex items-center gap-2">
                                  {user.name}
                                  {isUserAdmin && (
                                    <span className="text-[10px] bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">
                                      Admin
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-8 text-sm text-slate-500 dark:text-slate-400">
                              {user.email}
                            </td>
                            <td className="py-4 px-8 text-right">
                              {isMaintainer && (
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() =>
                                      setRoleModal({
                                        isOpen: true,
                                        type: "WORKSPACE",
                                        userId: user.id,
                                        currentRole: currentWorkspaceRole,
                                      })
                                    }
                                    className="flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-400 text-nowrap hover:text-indigo-600 dark:hover:text-indigo-400 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 rounded-lg shadow-sm transition"
                                  >
                                    <Settings2 size={14} /> Change role
                                  </button>
                                  <button
                                    onClick={() =>
                                      setRemoveConfirm({
                                        isOpen: true,
                                        type: "WORKSPACE",
                                        userId: user.id,
                                        userName: user.name,
                                      })
                                    }
                                    className="flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-red-600 dark:hover:text-red-400 text-nowrap px-2.5 py-1.5 transition"
                                  >
                                    <Trash2 size={14} /> Remove
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {invitations.map((invite) => (
                        <tr
                          key={invite.id}
                          className="group hover:bg-slate-50 dark:hover:bg-slate-700/30 transition"
                        >
                          <td className="py-4 px-8 flex items-center gap-3 italic">
                            <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-400">
                              <Mail size={14} />
                            </div>
                            <div className="flex flex-col">
                              <div className="text-sm font-medium text-slate-400 dark:text-slate-500 text-nowrap flex items-center gap-2">
                                Invited User
                                <span className="text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider border border-slate-200 dark:border-slate-700">
                                  Pending
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-8 text-sm text-slate-400 dark:text-slate-500 italic">
                            {invite.email}
                          </td>
                          <td className="py-4 px-8 text-right">
                            {isMaintainer && (
                              <div className="flex justify-end gap-2 items-center">
                                <span className="text-xs text-slate-400 italic">
                                  Invite sent
                                </span>
                                <button
                                  onClick={() => handleCancelInvite(invite.id)}
                                  className="flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-red-600 dark:hover:text-red-400 text-nowrap px-2.5 py-1.5 transition"
                                  title="Cancel Invitation"
                                >
                                  <Trash2 size={14} /> Remove
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )
        ) : activeTab === "activity" ? (
          currentOrg.tier !== "ENTERPRISE" ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm text-center px-6">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mb-6">
                <History
                  size={32}
                  className="text-slate-400 dark:text-slate-500"
                />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
                Audit Logs & Activity
              </h2>
              <p className="text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-8 text-lg">
                Track every action taken in your workspace for compliance and
                security. Available on the Enterprise plan.
              </p>
              <button
                onClick={
                  isPersonal
                    ? onOpenCreateWorkspace
                    : () => navigate(`/${currentOrg.slug}/workspace/billing`)
                }
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition shadow-lg shadow-indigo-500/20"
              >
                {isPersonal ? "Upgrade to Team Plan" : "Upgrade to Enterprise"}
              </button>
            </div>
          ) : (
            <div className="flex flex-col min-h-full bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
              <div className="p-6 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h2 className="font-bold text-slate-800 dark:text-white flex items-center gap-2">
                    <History
                      size={20}
                      className="text-indigo-600 dark:text-indigo-400"
                    />
                    Activity Log
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    A chronological history of actions in this workspace.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <select
                    value={activityAction}
                    onChange={(e) => setActivityAction(e.target.value)}
                    className="text-sm border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  >
                    <option value="">All Actions</option>
                    <optgroup label="Plans & Runs">
                      <option value="PLAN_CREATED">Plan Created</option>
                      <option value="PLAN_UPDATED">Plan Updated</option>
                      <option value="PLAN_DELETED">Plan Deleted</option>
                      <option value="RUN_STARTED">Run Started</option>
                      <option value="RUN_COMPLETED">Run Completed</option>
                      <option value="TASK_COMPLETED">Task Completed</option>
                      <option value="TASK_SKIPPED">Task Skipped</option>
                      <option value="TASK_RETRIED">Task Retried</option>
                    </optgroup>
                    <optgroup label="Members & Teams">
                      <option value="MEMBER_INVITED">Member Invited</option>
                      <option value="MEMBER_JOINED">Member Joined</option>
                      <option value="MEMBER_REMOVED">Member Removed</option>
                      <option value="TEAM_CREATED">Team Created</option>
                      <option value="TEAM_UPDATED">Team Updated</option>
                      <option value="TEAM_DELETED">Team Deleted</option>
                    </optgroup>
                  </select>
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-slate-900/30 px-6 py-2 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  Showing {activityLogs?.length || 0} of {activityTotal}{" "}
                  activities
                </span>
              </div>

              <div className="flex-1 overflow-y-auto">
                {activityLogs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                    <History size={48} className="mb-4 opacity-20" />
                    <p>
                      {isActivityLoading
                        ? "Loading activity..."
                        : "No activity found."}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {activityLogs.map((log) => (
                      <div
                        key={log.id}
                        className="p-4 hover:bg-slate-50 dark:hover:bg-slate-900/50 transition flex items-start gap-4"
                      >
                        <div className="mt-1 p-2 bg-slate-100 dark:bg-slate-800 rounded-lg shrink-0">
                          {getActivityIcon(log.action)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                            {getActivityMessage(log)}
                          </div>
                          <div className="text-xs text-slate-400 mt-1">
                            {new Date(log.createdAt).toLocaleString()}
                          </div>
                        </div>
                        {log.user?.imageUrl && (
                          <img
                            src={log.user.imageUrl}
                            alt={log.user.name}
                            className="w-6 h-6 rounded-full border border-white dark:border-slate-700 shadow-sm"
                          />
                        )}
                      </div>
                    ))}

                    {hasMoreActivity && (
                      <div className="p-4 flex justify-center">
                        <button
                          onClick={() =>
                            fetchActivity(activityOffset + 10, true)
                          }
                          disabled={isActivityLoading}
                          className="text-sm font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 disabled:opacity-50"
                        >
                          {isActivityLoading
                            ? "Loading..."
                            : "Load More Activity"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        ) : null}
      </div>
    </div>
  );
};

export default TeamManager;
